/**
 * AIStudio — AI 스튜디오 대시보드
 *
 * 앨범 0개 → StudioWizard 표시
 * 앨범 1개+ → 대시보드 (크레딧, 최근 사진, 앨범 목록, 생성 버튼)
 */

import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { Camera, ImagePlus } from 'lucide-react'
import { useAuth } from '../../lib/auth.js'
import { useApiFetch } from '../../hooks/useApiFetch.js'
import { useAlbums } from '../../hooks/useAlbums.js'
import { Button, Spinner, EmptyState, Badge, Modal } from '../../components/ui/index.js'
import { GenerateModal } from '../../features/ai-studio/components/GenerateModal.js'
import { BackgroundGenBanner } from '../../features/ai-studio/components/BackgroundGenBanner.js'
import { StudioWizard } from '../../features/ai-studio/components/StudioWizard.js'
import { ReferenceUpload } from '../../features/ai-studio/components/ReferenceUpload.js'
import { useReferencePhotos } from '../../features/ai-studio/hooks/useReferencePhotos.js'
import styles from './AIStudio.module.css'

interface Generation {
  id: string
  generatedUrls: string[] | null
  style: string | null
  createdAt: string
}

interface GenerationsResponse {
  generations: Generation[]
  pagination: { total: number }
}

export function AIStudio() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { albums, isLoading: albumsLoading } = useAlbums()
  const { data: genData, isLoading: genLoading } = useApiFetch<GenerationsResponse>('/api/ai/generations?pageSize=8')
  const refPhotos = useReferencePhotos()

  const [showGenerate, setShowGenerate] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  const recentPhotos = useMemo(() =>
    (genData?.generations ?? []).flatMap(g =>
      (g.generatedUrls ?? []).map(url => ({ url, id: g.id }))
    ),
    [genData?.generations],
  )

  // 로딩 중
  if (albumsLoading) {
    return (
      <div className={styles.root}>
        <div className={styles.loadingRow}><Spinner size="lg" /></div>
      </div>
    )
  }

  // 앨범 0개 → 위자드
  if (albums.length === 0) {
    return <StudioWizard />
  }

  // 대시보드
  return (
    <div className={styles.root}>
      {/* 헤더 */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>AI 스튜디오</h1>
          <p className={styles.creditInfo}>
            크레딧 <span className={styles.creditCount}>{user?.aiCredits ?? 0}</span>개
          </p>
        </div>
        <div className={styles.headerActions}>
          <Button size="sm" onClick={() => setShowGenerate(true)}>
            <ImagePlus size={16} /> 사진 생성하기
          </Button>
        </div>
      </div>

      {/* 백그라운드 생성 진행 배너 */}
      <BackgroundGenBanner />

      {/* 참조 사진 관리 */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>참조 사진</h2>
        </div>
        <ReferenceUpload
          photos={refPhotos.photos}
          uploading={refPhotos.uploading}
          onUpload={refPhotos.upload}
          onDelete={refPhotos.remove}
          getByRole={refPhotos.getByRole}
        />
      </section>

      {/* 앨범 목록 */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>촬영</h2>
          <Badge color="gray">{albums.length}개</Badge>
        </div>

        <div className={styles.albumGrid}>
          {albums.map(album => {
            const genUrls = (album.generations ?? []).flatMap(g => g.generatedUrls ?? [])
            const coverUrl = genUrls[0] ?? album.images[0]?.url
            const photoCount = genUrls.length || album.images.length
            return (
              <button
                key={album.id}
                className={styles.albumCard}
                onClick={() => navigate(`/studio/ai/albums/${album.id}`)}
              >
                <div className={styles.albumCover}>
                  {coverUrl ? (
                    <img src={coverUrl} alt="" className={styles.albumCoverImg} />
                  ) : (
                    <div className={styles.albumCoverEmpty}>
                      <Camera size={24} strokeWidth={1.5} />
                    </div>
                  )}
                </div>
                <div className={styles.albumInfo}>
                  <span className={styles.albumName}>{album.name}</span>
                  <span className={styles.albumMeta}>{photoCount}장</span>
                </div>
              </button>
            )
          })}
        </div>
      </section>

      {/* 최근 생성 사진 */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>최근 생성 사진</h2>
        </div>

        {genLoading ? (
          <div className={styles.loadingRow}><Spinner size="md" /></div>
        ) : recentPhotos.length === 0 ? (
          <EmptyState
            icon={<Camera size={32} strokeWidth={1.5} />}
            title="아직 생성된 사진이 없어요"
            description="사진 생성 버튼을 눌러 AI 웨딩 사진을 만들어보세요"
          />
        ) : (
          <div className={styles.photoScroll}>
            {recentPhotos.slice(0, 12).map(photo => (
              <button key={photo.url} className={styles.photoCard} onClick={() => setPreviewUrl(photo.url)}>
                <img src={photo.url} alt="" className={styles.photoImg} />
              </button>
            ))}
          </div>
        )}
      </section>

      <GenerateModal open={showGenerate} onClose={() => setShowGenerate(false)} />

      {/* 사진 크게보기 */}
      <Modal open={!!previewUrl} onClose={() => setPreviewUrl(null)} size="lg">
        {previewUrl && (
          <img
            src={previewUrl}
            alt="확대 보기"
            style={{ width: '100%', maxHeight: '80vh', objectFit: 'contain', borderRadius: 'var(--radius-md)' }}
          />
        )}
      </Modal>
    </div>
  )
}
